/*
 * Client-side Word (.doc) export for the Sunsri Brochure.
 * Uses the standard "HTML wrapped in Word MHT-style meta" approach.
 * Microsoft Word opens the file natively; users can then re-save as .docx.
 * No external dependency required.
 */

const wordStyles = `
  @page { size: A4; margin: 2cm; }
  body { font-family: 'Georgia', 'Times New Roman', serif; color: #0F172A; line-height: 1.55; }
  h1 { font-family: 'Georgia', serif; font-size: 32pt; color: #0B1220; margin: 0 0 6pt; letter-spacing: -0.5pt; }
  h2 { font-family: 'Georgia', serif; font-size: 20pt; color: #0B1220; margin: 24pt 0 8pt; border-bottom: 1pt solid #C9A24E; padding-bottom: 4pt; }
  h3 { font-family: 'Georgia', serif; font-size: 14pt; color: #0F172A; margin: 14pt 0 4pt; }
  h4 { font-family: 'Arial', sans-serif; font-size: 11pt; color: #C9A24E; text-transform: uppercase; letter-spacing: 2pt; margin: 18pt 0 6pt; }
  p  { font-family: 'Georgia', serif; font-size: 11pt; margin: 0 0 8pt; }
  ul { margin: 4pt 0 12pt 18pt; }
  li { font-family: 'Georgia', serif; font-size: 11pt; margin: 0 0 4pt; }
  .cover { text-align: center; padding: 60pt 0; border-bottom: 2pt solid #0B1220; margin-bottom: 24pt; }
  .cover-title { font-size: 42pt; letter-spacing: 3pt; text-transform: uppercase; }
  .cover-url { font-family: 'Arial', sans-serif; font-size: 10pt; letter-spacing: 4pt; color: #C9A24E; text-transform: uppercase; margin-top: 12pt; }
  .stats td { padding: 12pt; border: 1pt solid #E5E7EB; text-align: center; width: 25%; vertical-align: middle; }
  .stat-num { font-family: 'Georgia', serif; font-size: 28pt; color: #0B1220; }
  .stat-lbl { font-family: 'Arial', sans-serif; font-size: 9pt; letter-spacing: 2pt; color: #6B7280; text-transform: uppercase; }
  .services td { padding: 10pt; border: 1pt solid #E5E7EB; vertical-align: top; width: 33%; }
  .solutions td { padding: 10pt; border: 1pt solid #C9A24E; vertical-align: middle; width: 33%; text-align: center; background: #FAFAFA; }
  .contact { border-top: 2pt solid #C9A24E; margin-top: 30pt; padding-top: 20pt; }
  .accent { color: #C9A24E; }
  .subtitle { font-family: 'Arial', sans-serif; font-size: 11pt; color: #6B7280; letter-spacing: 3pt; text-transform: uppercase; }
`;

const stat = (num, lbl) =>
  `<td><div class="stat-num">${num}</div><div class="stat-lbl">${lbl}</div></td>`;

const service = (title, body) =>
  `<td><h3>${title}</h3><p>${body}</p></td>`;

const solution = (name) => `<td><h3 style="margin:0">${name}</h3></td>`;

function buildBody() {
  return `
    <div class="cover">
      <div class="subtitle">Sunsri Software Private Limited</div>
      <h1 class="cover-title">Intelligent<br/>SAP Solutions</h1>
      <p style="font-size:13pt;font-style:italic;margin-top:14pt">Scalable, Secure, and Smart — SAP Solutions for Success</p>
      <div class="cover-url">www.sunsris.com</div>
    </div>

    <h4>Your Partner for Software Innovation</h4>
    <p>We specialize in delivering comprehensive SAP solutions tailored to drive business transformation and innovation. With years of expertise in SAP implementation, support, and enhancement, we empower organizations to optimize their processes, improve efficiency, and achieve measurable results.</p>
    <p>Our team of certified SAP professionals brings a blend of technical expertise, industry knowledge, and a passion for problem-solving. From small enterprises to large global corporations, we partner with businesses across diverse industries to design and deploy customized SAP solutions that align with their unique objectives.</p>

    <h2>Mission &amp; Vision</h2>
    <h4>Mission</h4>
    <p>We are committed to delivering value-driven SAP solutions that help businesses:</p>
    <ul>
      <li>Simplify complex operations</li>
      <li>Drive sustainable growth</li>
      <li>Adapt seamlessly to evolving market dynamics</li>
    </ul>
    <h4>Vision</h4>
    <p>To be a trusted leader in SAP services, enabling businesses to unlock their full potential through innovative technology and strategic insights.</p>

    <h2>By the Numbers</h2>
    <table class="stats" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse">
      <tr>
        ${stat("30+", "SAP SMEs")}
        ${stat("10+", "Industry Expertise")}
        ${stat("20+", "Solution Templates")}
        ${stat("25+", "Years of Experience")}
      </tr>
    </table>

    <h2>Industries We Serve</h2>
    <ul style="columns:2;-webkit-columns:2;column-gap:24pt">
      <li>Public Sector</li>
      <li>Banking &amp; Finance</li>
      <li>Manufacturing</li>
      <li>Telecom</li>
      <li>Travel</li>
      <li>Retail</li>
      <li>Healthcare</li>
      <li>Dairy</li>
      <li>Textile</li>
      <li>Energy &amp; Utilities</li>
    </ul>

    <h2>Proven Expertise &nbsp;|&nbsp; Global Reach &nbsp;|&nbsp; Continuous Innovation</h2>
    <table class="services" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse">
      <tr>
        ${service("Implementations", "Our proven SAP methodology delivers projects on time, within budget, and to your exact specifications through a structured, phased approach for business success.")}
        ${service("Migrations", "Our proven SAP methodology delivers projects on time, within budget, and to your exact specifications through a structured, phased approach for business success.")}
        ${service("Support", "Our SAP support team offers expert assistance across all modules, ensuring peak performance, smooth operation and continuous system improvement.")}
      </tr>
      <tr>
        ${service("Application Development", "Our tailored SAP development and integration ensure your solutions align with current operations, support future growth, and harmonize with business requirements.")}
        ${service("Integration", "Our proven SAP methodology delivers projects on time, within budget, and to your exact specifications through a structured, phased approach for business success.")}
        ${service("Roll Out", "Our SAP rollout services help organizations standardize processes, achieve faster deployments, and ensure alignment with global business objectives.")}
      </tr>
      <tr>
        ${service("Upgrades", "Our SAP upgrade services are designed to deliver a seamless transition to newer SAP versions, whether it's a technical upgrade, a functional enhancement, or a full migration to SAP S/4HANA.")}
        ${service("Testing", "Our SAP Testing Services are designed to provide comprehensive, end-to-end testing solutions that ensure the performance, reliability, and scalability of your SAP applications.")}
        ${service("Training", "By providing tailored learning experiences, we ensure your teams can efficiently leverage SAP's capabilities to meet your business objectives.")}
      </tr>
    </table>

    <h2>Our SAP Solutions &amp; Offerings</h2>
    <p style="font-style:italic;color:#6B7280;margin-top:-4pt">Empowering business with tailored SAP solutions and expert services.</p>
    <table class="solutions" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;margin-top:8pt">
      <tr>
        ${solution("SAP S/4HANA Private Cloud")}
        ${solution("SAP S/4HANA Public Cloud")}
        ${solution("SAP Ariba")}
      </tr>
      <tr>
        ${solution("SAP SuccessFactors")}
        ${solution("SAP Commerce Cloud")}
        ${solution("SAP Concur")}
      </tr>
      <tr>
        ${solution("SAP Manufacturing")}
        ${solution("SAP Fieldglass")}
        ${solution("SAP Digital Manufacturing")}
      </tr>
      <tr>
        ${solution("SAP Business Networks")}
        ${solution("SAP HXM")}
        ${solution("SAP Mobility")}
      </tr>
      <tr>
        ${solution("SAP Field Service Mgmt.")}
        ${solution("SAP Product Lifecycle Mgmt.")}
        ${solution("SAP EWM")}
      </tr>
    </table>

    <div class="contact">
      <h2 style="border:none;padding:0">Ready to grow your business?</h2>
      <h4>Contact Us</h4>
      <p><strong>Our Address:</strong> 301 &amp; 302, Surya Pearl Building, Opp: Raheja Mindspace IT Park, Hitech City, Hyderabad – 500081</p>
      <p><strong>Our Mailbox:</strong> info@sunsrisoft.com</p>
      <p><strong>Our Phone:</strong> +91 - 87900 06313</p>
      <p class="accent" style="margin-top:14pt;letter-spacing:3pt;text-transform:uppercase;font-family:Arial,sans-serif;font-size:10pt">www.sunsris.com</p>
    </div>
  `;
}

export function downloadBrochureDoc(filename = "Sunsri-SAP-Brochure.doc") {
  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="utf-8">
      <title>Sunsri Software — SAP Brochure</title>
      <!--[if gte mso 9]>
      <xml>
        <w:WordDocument>
          <w:View>Print</w:View>
          <w:Zoom>100</w:Zoom>
          <w:DoNotOptimizeForBrowser/>
        </w:WordDocument>
      </xml>
      <![endif]-->
      <style>${wordStyles}</style>
    </head>
    <body>${buildBody()}</body>
    </html>
  `;

  const blob = new Blob(["\ufeff", html], {
    type: "application/msword;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}
